<script setup>
import { computed, onMounted, ref } from 'vue';
import { useMapGetter, useStore } from 'dashboard/composables/store.js';
import { useAccount } from 'dashboard/composables/useAccount';
import { useI18n } from 'vue-i18n';
import { format } from 'date-fns';

import BillingCard from './components/BillingCard.vue';
import BillingHeader from './components/BillingHeader.vue';
import DetailItem from './components/DetailItem.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import ButtonV4 from 'next/button/Button.vue';

const { currentAccount } = useAccount();
const { t, locale } = useI18n();
const uiFlags = useMapGetter('accounts/getUIFlags');
const store = useStore();
const subscriptionCancelResponse = ref(null);
const customAttributes = computed(() => {
  return currentAccount.value.custom_attributes || {};
});
const aiBilling = computed(() => currentAccount.value?.ai_billing || null);
const aiRecent7d = computed(() => aiBilling.value?.recent_7d || []);

const formatCurrency = (value, currency = 'USD') => {
  const numeric = Number(value || 0);
  const safeValue = Number.isFinite(numeric) ? numeric : 0;
  return `${safeValue.toFixed(2)} ${currency}`;
};

const formatInteger = value => {
  const numeric = Number(value || 0);
  return Number.isFinite(numeric) ? `${Math.max(0, Math.floor(numeric))}` : '0';
};

const formatDay = value => {
  if (!value) return '-';
  const parsedDate = new Date(`${value}T00:00:00`);
  if (Number.isNaN(parsedDate.getTime())) return value;
  return format(parsedDate, 'MM/dd');
};

const isChineseLocale = computed(() => String(locale.value).startsWith('zh'));

const formatBillingDate = value => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return format(date, isChineseLocale.value ? 'yyyy/MM/dd' : 'dd MMM, yyyy');
};

const formatBillingDateTime = value => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return format(
    date,
    isChineseLocale.value ? 'yyyy/MM/dd HH:mm' : 'dd MMM, yyyy HH:mm'
  );
};

const aiMonthlyCost = computed(() => {
  if (!aiBilling.value) return '';
  return formatCurrency(aiBilling.value.monthly_spent_twd, 'TWD');
});

const aiMonthlyBudget = computed(() => {
  if (!aiBilling.value) return '';
  return formatCurrency(aiBilling.value.monthly_budget_usd, 'USD');
});

const aiTopupBalance = computed(() => {
  if (!aiBilling.value) return '';
  return formatCurrency(aiBilling.value.credit_balance_usd, 'USD');
});

const aiHardLimitRemaining = computed(() => {
  if (!aiBilling.value) return '';
  return formatCurrency(aiBilling.value.hard_limit_remaining_usd, 'USD');
});

const aiTokensUsage = computed(() => {
  if (!aiBilling.value) return '';
  const used = formatInteger(aiBilling.value.token_used);
  const quota = formatInteger(aiBilling.value.token_quota);
  return `${used} / ${quota}`;
});

/**
 * Computed property for plan name
 * @returns {string|undefined}
 */
const planName = computed(() => {
  return customAttributes.value.plan_name;
});

const effectivePlanName = computed(() => {
  return planName.value || currentAccount.value?.plan || '';
});

/**
 * Computed property for subscribed quantity
 * @returns {number|undefined}
 */
const subscribedQuantity = computed(() => {
  return (
    customAttributes.value.subscribed_quantity ||
    currentAccount.value?.seats_limit
  );
});

const subscriptionRenewsOn = computed(() => {
  const dateValue =
    customAttributes.value.subscription_ends_on ||
    currentAccount.value?.current_period_end;
  if (!dateValue) return '';
  return formatBillingDate(dateValue);
});

const subscriptionCancelStatus = computed(() => {
  return (
    subscriptionCancelResponse.value?.subscription_cancel_status ||
    customAttributes.value.subscription_cancel_status ||
    ''
  );
});

const subscriptionCancelRequested = computed(() => {
  return ['requested', 'cancelled'].includes(subscriptionCancelStatus.value);
});

const subscriptionCancelActionLabel = computed(() => {
  if (subscriptionCancelStatus.value === 'cancelled') {
    return t('BILLING_SETTINGS.CANCEL_SUBSCRIPTION.CANCELLED_BUTTON');
  }
  if (subscriptionCancelStatus.value === 'requested') {
    return t('BILLING_SETTINGS.CANCEL_SUBSCRIPTION.REQUESTED_BUTTON');
  }
  if (subscriptionCancelStatus.value === 'cancel_failed') {
    return t('BILLING_SETTINGS.CANCEL_SUBSCRIPTION.RETRY_BUTTON');
  }
  return t('BILLING_SETTINGS.CANCEL_SUBSCRIPTION.BUTTON');
});

const subscriptionCancelStatusLabel = computed(() => {
  if (subscriptionCancelStatus.value === 'cancelled') {
    return t('BILLING_SETTINGS.CANCEL_SUBSCRIPTION.STATUS.CANCELLED');
  }
  if (subscriptionCancelStatus.value === 'requested') {
    return t('BILLING_SETTINGS.CANCEL_SUBSCRIPTION.STATUS.REQUESTED');
  }
  if (subscriptionCancelStatus.value === 'cancel_failed') {
    return t('BILLING_SETTINGS.CANCEL_SUBSCRIPTION.STATUS.FAILED');
  }
  return '';
});

const subscriptionCancelRequestedAt = computed(() => {
  const dateValue =
    subscriptionCancelResponse.value?.subscription_cancelled_at ||
    subscriptionCancelResponse.value?.subscription_cancel_requested_at ||
    customAttributes.value.subscription_cancelled_at ||
    customAttributes.value.subscription_cancel_requested_at;
  if (!dateValue) return '';
  return formatBillingDateTime(dateValue);
});

const subscriptionCancelDisplayValue = computed(() => {
  if (!subscriptionCancelStatusLabel.value) return '';
  return [
    subscriptionCancelStatusLabel.value,
    subscriptionCancelRequestedAt.value,
  ]
    .filter(Boolean)
    .join(' / ');
});

/**
 * Computed property indicating if user has a billing plan
 * @returns {boolean}
 */
const hasABillingPlan = computed(() => {
  return !!effectivePlanName.value;
});

const fetchAccountDetails = async () => {
  if (!hasABillingPlan.value) {
    store.dispatch('accounts/subscription');
  }
};

const onClickBillingPortal = () => {
  store.dispatch('accounts/checkout');
};

const onCancelSubscription = async () => {
  if (subscriptionCancelRequested.value) return;

  const confirmed = window.confirm(
    t('BILLING_SETTINGS.CANCEL_SUBSCRIPTION.CONFIRM_MESSAGE')
  );
  if (!confirmed) return;

  const response = await store.dispatch('accounts/cancelSubscription');
  subscriptionCancelResponse.value = response.data;
};

const onToggleChatWindow = () => {
  if (window.$chatwoot) {
    window.$chatwoot.toggle();
  }
};

onMounted(fetchAccountDetails);
</script>

<template>
  <SettingsLayout
    :is-loading="uiFlags.isFetchingItem"
    :loading-message="$t('ATTRIBUTES_MGMT.LOADING')"
    :no-records-found="!hasABillingPlan"
    :no-records-message="$t('BILLING_SETTINGS.NO_BILLING_USER')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="$t('BILLING_SETTINGS.TITLE')"
        :description="$t('BILLING_SETTINGS.DESCRIPTION')"
        :link-text="$t('BILLING_SETTINGS.VIEW_PRICING')"
        feature-name="billing"
      />
    </template>
    <template #body>
      <section class="grid gap-4">
        <BillingCard
          :title="$t('BILLING_SETTINGS.MANAGE_SUBSCRIPTION.TITLE')"
          :description="$t('BILLING_SETTINGS.MANAGE_SUBSCRIPTION.DESCRIPTION')"
        >
          <template #action>
            <div class="flex gap-2 flex-wrap justify-end">
              <ButtonV4 sm solid blue @click="onClickBillingPortal">
                {{ $t('BILLING_SETTINGS.MANAGE_SUBSCRIPTION.BUTTON_TXT') }}
              </ButtonV4>
              <ButtonV4
                sm
                outline
                ruby
                :disabled="
                  subscriptionCancelRequested || uiFlags.isCheckoutInProcess
                "
                :is-loading="uiFlags.isCheckoutInProcess"
                @click="onCancelSubscription"
              >
                {{ subscriptionCancelActionLabel }}
              </ButtonV4>
            </div>
          </template>
          <div
            v-if="
              effectivePlanName || subscribedQuantity || subscriptionRenewsOn
            "
            class="grid lg:grid-cols-4 sm:grid-cols-3 grid-cols-1 gap-2 divide-x divide-n-weak"
          >
            <DetailItem
              :label="$t('BILLING_SETTINGS.CURRENT_PLAN.TITLE')"
              :value="effectivePlanName"
            />
            <DetailItem
              v-if="subscribedQuantity"
              :label="$t('BILLING_SETTINGS.CURRENT_PLAN.SEAT_COUNT')"
              :value="subscribedQuantity"
            />
            <DetailItem
              v-if="subscriptionRenewsOn"
              :label="$t('BILLING_SETTINGS.CURRENT_PLAN.RENEWS_ON')"
              :value="subscriptionRenewsOn"
            />
            <DetailItem
              v-if="subscriptionCancelDisplayValue"
              :label="$t('BILLING_SETTINGS.CANCEL_SUBSCRIPTION.STATUS_LABEL')"
              :value="subscriptionCancelDisplayValue"
            />
          </div>
        </BillingCard>
        <BillingCard
          v-if="aiBilling"
          :title="$t('BILLING_SETTINGS.AI_USAGE.TITLE')"
          :description="$t('BILLING_SETTINGS.AI_USAGE.DESCRIPTION')"
        >
          <div
            class="grid lg:grid-cols-5 sm:grid-cols-2 grid-cols-1 gap-2 divide-x divide-n-weak"
          >
            <DetailItem
              :label="$t('BILLING_SETTINGS.AI_USAGE.MONTHLY_COST')"
              :value="aiMonthlyCost"
            />
            <DetailItem
              :label="$t('BILLING_SETTINGS.AI_USAGE.PLAN_BUDGET')"
              :value="aiMonthlyBudget"
            />
            <DetailItem
              :label="$t('BILLING_SETTINGS.AI_USAGE.TOPUP_BALANCE')"
              :value="aiTopupBalance"
            />
            <DetailItem
              :label="$t('BILLING_SETTINGS.AI_USAGE.HARD_LIMIT_REMAINING')"
              :value="aiHardLimitRemaining"
            />
            <DetailItem
              :label="$t('BILLING_SETTINGS.AI_USAGE.TOKENS_USAGE')"
              :value="aiTokensUsage"
            />
          </div>
        </BillingCard>
        <BillingCard
          v-if="aiBilling"
          :title="$t('BILLING_SETTINGS.AI_RECENT_USAGE.TITLE')"
          :description="$t('BILLING_SETTINGS.AI_RECENT_USAGE.DESCRIPTION')"
        >
          <div class="overflow-x-auto px-5">
            <table class="w-full text-left text-sm">
              <thead>
                <tr class="text-n-slate-11 border-b border-n-weak">
                  <th class="py-2 pr-3 font-medium">
                    {{ $t('BILLING_SETTINGS.AI_RECENT_USAGE.DATE') }}
                  </th>
                  <th class="py-2 pr-3 font-medium">
                    {{ $t('BILLING_SETTINGS.AI_RECENT_USAGE.COST_TWD') }}
                  </th>
                  <th class="py-2 pr-3 font-medium">
                    {{ $t('BILLING_SETTINGS.AI_RECENT_USAGE.COST_USD') }}
                  </th>
                  <th class="py-2 pr-3 font-medium">
                    {{ $t('BILLING_SETTINGS.AI_RECENT_USAGE.INPUT') }}
                  </th>
                  <th class="py-2 pr-3 font-medium">
                    {{ $t('BILLING_SETTINGS.AI_RECENT_USAGE.OUTPUT') }}
                  </th>
                  <th class="py-2 pr-3 font-medium">
                    {{ $t('BILLING_SETTINGS.AI_RECENT_USAGE.TOTAL') }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="row in aiRecent7d"
                  :key="row.date"
                  class="border-b border-n-weak text-n-slate-12"
                >
                  <td class="py-2 pr-3">{{ formatDay(row.date) }}</td>
                  <td class="py-2 pr-3">
                    {{ formatCurrency(row.cost_twd, 'TWD') }}
                  </td>
                  <td class="py-2 pr-3">
                    {{ formatCurrency(row.cost_usd, 'USD') }}
                  </td>
                  <td class="py-2 pr-3">
                    {{ formatInteger(row.input_tokens) }}
                  </td>
                  <td class="py-2 pr-3">
                    {{ formatInteger(row.output_tokens) }}
                  </td>
                  <td class="py-2 pr-3">
                    {{ formatInteger(row.total_tokens) }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </BillingCard>
        <BillingHeader
          class="px-1 mt-5"
          :title="$t('BILLING_SETTINGS.CHAT_WITH_US.TITLE')"
          :description="$t('BILLING_SETTINGS.CHAT_WITH_US.DESCRIPTION')"
        >
          <ButtonV4
            sm
            solid
            slate
            icon="i-lucide-life-buoy"
            @click="onToggleChatWindow"
          >
            {{ $t('BILLING_SETTINGS.CHAT_WITH_US.BUTTON_TXT') }}
          </ButtonV4>
        </BillingHeader>
      </section>
    </template>
  </SettingsLayout>
</template>
